import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import SvgToIcon from "../../icons/SvgToIcon";
import {
  detectionIcons,
  icons,
  jiraPriorities,
  logos,
  operationalHeatmap,
  severities,
  sidebarIcons,
} from "../../scenes/stack/environment/icons/iconExport";

/**
 * Icon Gallery component - displays all available icons in the system
 * Allows users to search, filter, and copy the component code by clicking on an icon
 */
const IconGalleryDemo = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [copySnackbar, setCopySnackbar] = useState({
    open: false,
    message: "",
  });

  // All icon categories with readable display names
  const categories = [
    { id: "all", label: "All Icons" },
    { id: "icons", label: "General Icons" },
    { id: "logos", label: "Logos" },
    { id: "severities", label: "Severities" },
    { id: "jiraPriorities", label: "Jira Priorities" },
    { id: "detectionIcons", label: "Detection Icons" },
    { id: "sidebarIcons", label: "Sidebar Icons" },
    { id: "operationalHeatmap", label: "Operational Heatmap Icons" },
  ];

  // Icon collections with display names
  const iconCollections = {
    icons: { data: icons, displayName: "General Icons" },
    logos: { data: logos, displayName: "Logos" },
    severities: { data: severities, displayName: "Severities" },
    jiraPriorities: { data: jiraPriorities, displayName: "Jira Priorities" },
    detectionIcons: { data: detectionIcons, displayName: "Detection Icons" },
    sidebarIcons: { data: sidebarIcons, displayName: "Sidebar Icons" },
    operationalHeatmap: {
      data: operationalHeatmap,
      displayName: "Operational Heatmap Icons",
    },
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  // Handle category selection
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  // Copy icon usage code to clipboard
  const handleCopyCode = (collectionName, iconName) => {
    const codeSnippet = `<SvgToIcon IconLogo={${collectionName}.${iconName}} />`;
    navigator.clipboard.writeText(codeSnippet);
    setCopySnackbar({ open: true, message: `Copied: ${codeSnippet}` });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setCopySnackbar({ ...copySnackbar, open: false });
  };

  // Render all icons or filtered by category
  const renderIconGroups = () => {
    let sections = [];

    // Determine which collections to render based on selected category
    const collectionsToRender =
      selectedCategory === "all"
        ? Object.keys(iconCollections)
        : [selectedCategory];

    // Render each collection as a separate section
    collectionsToRender.forEach((collectionName) => {
      const collection = iconCollections[collectionName];
      const iconEntries = Object.entries(collection.data);

      // Skip empty collections or if no icons match search
      const filteredIcons = iconEntries.filter(
        ([iconName]) =>
          !searchTerm || iconName.toLowerCase().includes(searchTerm)
      );

      if (filteredIcons.length === 0) return;

      // Add section with title and icons
      sections.push(
        <Box key={collectionName} sx={{ mb: 5 }}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.1rem",
                fontWeight: 500,
                color: "primary.main",
                p: 0.5,
                borderBottom: "2px solid",
                borderColor: "primary.light",
                display: "inline-block",
              }}
            >
              {collection.displayName}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {filteredIcons.map(([iconName, IconComponent]) => {
              // Some SVGs have issues being rendered directly, so we'll wrap them safely
              const IconDisplay = () => {
                try {
                  return (
                    <SvgToIcon
                      IconLogo={IconComponent}
                      width="32px"
                      height="32px"
                    />
                  );
                } catch (e) {
                  return (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "grey.300",
                        borderRadius: 1,
                      }}
                    />
                  );
                }
              };

              return (
                <Grid
                  item
                  xs={4}
                  sm={3}
                  md={2}
                  lg={1}
                  key={`${collectionName}-${iconName}`}
                >
                  <Tooltip title={iconName} placement="top" arrow>
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "1/1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 16px",
                        transition: "all 0.2s",
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: 3,
                          transform: "translateY(-2px)",
                          bgcolor: "action.hover",
                        },
                        "&:active": {
                          transform: "translateY(0px)",
                          bgcolor: "action.selected",
                        },
                      }}
                      onClick={() => handleCopyCode(collectionName, iconName)}
                    >
                      <IconDisplay />
                    </Box>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      );
    });

    return sections.length > 0 ? (
      sections
    ) : (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">
          No icons found matching your search.
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ mb: 4 }}>
        <TextField
          placeholder="Search icons..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={
                selectedCategory === category.id ? "contained" : "outlined"
              }
              size="small"
              onClick={() => handleCategoryChange(category.id)}
              sx={{ textTransform: "none" }}
            >
              {category.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Icon Groups */}
      {renderIconGroups()}

      {/* Copy Notification */}
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={copySnackbar.message}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleCloseSnackbar}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default IconGalleryDemo;
